<template>
  <div class="min-h-screen bg-[#F9FAFB] pb-28">
    <!-- Header -->
    <div class="sticky top-0 z-10 bg-[#F9FAFB] px-4 pt-4 pb-2">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 grid place-items-center"
          @click="goBack"
        >
          <i class="fas fa-arrow-left text-gray-800" />
        </button>
        <h1 class="text-[17px] font-normal text-gray-900">
          Rewards
        </h1>
      </div>
    </div>

    <!-- Total Points Card -->
    <section class="px-4">
      <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 relative">
        <span class="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-semibold">
          Total Poin
        </span>

        <div class="flex items-start justify-between mt-2">
          <div class="leading-tight">
            <div class="text-[34px] font-bold text-[#0B63F6] tracking-tight">
              {{ totalXP }} XP
            </div>
          </div>
          <div class="text-3xl -mt-1">
            XP
          </div>
        </div>

        <!-- Progress -->
        <div class="mt-2">
          <div class="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              class="h-2 bg-[#2A7BF6] rounded-full transition-all"
              :style="{ width: topPercent + '%' }"
            />
          </div>

          <div class="mt-2 flex items-center justify-between text-[13px] text-gray-700">
            <div class="flex items-center gap-1">
              <span>{{ totalXP }}</span>
              <span>/</span>
              <span>{{ goalXP }}</span>
              <span class="-mt-[2px]">XP</span>
            </div>
            <span class="text-gray-500">{{ topPercent }}%</span>
          </div>

          <p class="mt-2 text-[12px] text-gray-500">
            Rewards You Can Get With Your Points
          </p>
        </div>
      </div>
    </section>

    <!-- Rewards List -->
    <section class="px-4 mt-5">
      <h2 class="text-[15px] font-semibold text-gray-900 mb-3">
        Rewards to Get
      </h2>

      <div class="space-y-4">
        <article
          v-for="r in rewards"
          :key="r.id"
          :class="[
            'rounded-2xl border p-4 shadow-sm',
            r.claimable ? 'bg-[#F2F7FF] border-blue-100' : 'bg-white border-gray-100'
          ]"
        >
          <div class="flex items-start gap-3">
            <!-- thumb -->
            <div class="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
              <img
                v-if="r.image"
                :src="r.image"
                class="w-full h-full object-cover"
              >
              <span
                v-else
                class="text-xl"
              >XP</span>
            </div>

            <div class="flex-1">
              <div class="flex items-start justify-between">
                <h3 class="text-[15px] font-semibold text-gray-900 leading-tight">
                  {{ r.title }}
                </h3>

                <!-- check on right -->
                <button
                  class="w-6 h-6 rounded-md grid place-items-center border"
                  :class="r.claimable ? 'bg-[#0B63F6] border-[#0B63F6] text-white' : 'bg-white border-gray-300 text-transparent'"
                  @click="toggleSelect(r)"
                >
                  <i class="fas fa-check text-[11px]" />
                </button>
              </div>

              <p class="text-[12px] text-gray-500 mt-0.5">
                {{ r.requirement }}
              </p>

              <!-- cost + percent -->
              <div class="flex items-center justify-between mt-3 text-[13px]">
                <span class="font-semibold text-gray-900">{{ r.cost }} XP</span>
                <span :class="r.claimable ? 'text-[#0B63F6]' : 'text-gray-500'">{{ Math.round(r.progress * 100) }}%</span>
              </div>

              <!-- progress bar -->
              <div
                class="mt-2 h-2 w-full rounded-full overflow-hidden"
                :class="r.claimable ? 'bg-blue-100' : 'bg-gray-200'"
              >
                <div
                  class="h-2 rounded-full"
                  :class="r.claimable ? 'bg-[#0B63F6]' : 'bg-gray-400'"
                  :style="{ width: (r.progress * 100) + '%' }"
                />
              </div>

              <!-- redeem button -->
              <button
                class="mt-3 w-full py-2.5 rounded-xl text-[14px] font-semibold transition"
                :class="
                  r.claimable
                    ? redeemingRewardId === r.id
                      ? 'bg-[#0B63F6]/70 text-white cursor-wait'
                      : 'bg-[#0B63F6] text-white'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                "
                :disabled="!r.claimable || redeemingRewardId === r.id"
                @click="redeem(r)"
              >
                {{ redeemingRewardId === r.id ? 'Redeeming...' : 'Redeem' }}
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- Toast -->
    <transition name="fade">
      <div
        v-if="toast.show"
        class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-black/80 text-white text-sm px-3 py-2 rounded-lg"
      >
        {{ toast.message }}
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { getRewardsByStudentId, redeemReward } from "@/api/rewardApi";

const router = useRouter();
const auth = useAuthStore();
function goBack() {
  router.back();
}

const rewardSummary = ref(null);
const rewards = ref([]);
const totalXP = ref(0);
const goalXP = ref(500);
const redeemingRewardId = ref(null);

const topPercent = computed(() => {
  const goal = Number(goalXP.value) || 0;
  if (goal <= 0) return 0;
  const percentage = Math.round((Number(totalXP.value || 0) / goal) * 100);
  return Math.min(Math.max(percentage, 0), 100);
});

const fetchRewardsSummary = async () => {
  if (!auth?.userId) return;
  try {
    const data = await getRewardsByStudentId(auth.userId);
    hydrateRewards(data);
  } catch (error) {
    console.error("Failed to load rewards:", error);
    rewardSummary.value = null;
    rewards.value = [];
    totalXP.value = 0;
    goalXP.value = 500;
  }
};

function hydrateRewards(summary) {
  rewardSummary.value = summary ?? null;
  const availableXp = Number(summary?.availableXp ?? 0);
  totalXP.value = availableXp;

  const rawRewards = Array.isArray(summary?.rewards) ? summary.rewards : [];
  const sorted = rawRewards
    .filter((item) => item)
    .map((item) => ({
      ...item,
      costXp: Number(item?.costXp ?? 0),
    }))
    .sort((a, b) => a.costXp - b.costXp);

  rewards.value = sorted.map((reward) => {
    const cost = reward.costXp || 0;
    const progressValue =
      typeof reward.progress === "number"
        ? reward.progress
        : cost > 0
        ? availableXp / cost
        : 0;
    const normalizedProgress = Math.min(Math.max(progressValue, 0), 1);

    let requirement = reward.description || "Keep going to unlock this reward.";
    if (reward.canRedeem) {
      requirement = "Rewards can be redeemed now!";
    } else if (reward.requiredQuizCount) {
      requirement = `Complete ${reward.requiredQuizCount} quizzes`;
    } else if (cost > availableXp) {
      const remaining = Math.max(Math.ceil(cost - availableXp), 0);
      requirement = `Need ${remaining} more XP`;
    }

    return {
      id: reward.id ?? reward.rewardId ?? reward.RewardId ?? reward.Id,
      title: reward.name ?? reward.title ?? "Reward",
      description: reward.description ?? "",
      image: reward.imageUrl ?? reward.image ?? null,
      cost,
      claimable: Boolean(reward.canRedeem),
      progress: normalizedProgress,
      requirement,
      selected: Boolean(reward.canRedeem),
    };
  });

  const targetReward =
    rewards.value.find((reward) => reward.cost > availableXp) ??
    rewards.value[rewards.value.length - 1];
  const nextGoal = targetReward?.cost ?? 0;
  goalXP.value = nextGoal > 0 ? nextGoal : 500;
}

async function redeem(reward) {
  if (!reward?.claimable || redeemingRewardId.value === reward.id) return;
  if (!auth?.userId) {
    showToast("Please log in to redeem rewards.");
    return;
  }

  try {
    redeemingRewardId.value = reward.id;
    await redeemReward({
      studentId: auth.userId,
      rewardId: reward.id,
      quantity: 1,
      requestedBy: auth.userId,
    });
    showToast(`Redeemed: ${reward.title}`);
    await fetchRewardsSummary();
  } catch (error) {
    console.error("Failed to redeem reward:", error);
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to redeem reward.";
    showToast(message);
  } finally {
    redeemingRewardId.value = null;
  }
}

function toggleSelect(reward) {
  if (!reward?.claimable) return;
  reward.selected = !reward.selected;
}

const toast = reactive({ show: false, message: "" });
let toastTimer;
function showToast(message) {
  toast.message = message;
  toast.show = true;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.show = false;
  }, 1600);
}

watch(
  () => auth.userId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchRewardsSummary();
    }
    if (!newId) {
      rewardSummary.value = null;
      rewards.value = [];
      totalXP.value = 0;
      goalXP.value = 500;
    }
  }
);

onMounted(fetchRewardsSummary);
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
